import React from "react";
import { Link } from "react-router-dom";
import { formatSAR } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useFavorites } from "../lib/favorites";
import { useAuth } from "../lib/auth";
import { localizeValue } from "../lib/displayValues";
import { Heart, ArrowUpRight, ShieldCheck, Sparkles, Gavel, Building2, CheckCircle2 } from "lucide-react";
import { FEATURES } from "../lib/features";

export default function VehicleCard({ v }) {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const { isFavorite, toggle } = useFavorites();
  const isRental = v.type === "rental";
  // Auctions are hidden site-wide while the feature flag is off — treat any
  // auction listing as a normal fixed-price listing on cards.
  const isAuction = FEATURES.AUCTIONS_ENABLED && v.listing_type === "auction";
  const fav = user ? isFavorite(v.id) : false;
  const periodicValid = Boolean(v.periodic_inspection_valid);
  const escrowEnabled = Boolean(v.escrow_enabled);
  const sellerVerified = Boolean(v.seller_verified);
  // Post-sale trust proof — set on the vehicle when a CARTAL Guarantee
  // (escrow authorization hold) has been successfully captured for it.
  // Also lit up for future listings by sellers with >=1 completed
  // guarantee deal.
  const escrowVerified =
    Boolean(v.escrow_verified) ||
    Number(v.seller_escrow_deals_completed || 0) > 0 ||
    Number(v.business_escrow_deals_completed || 0) > 0;
  const boosted = Boolean(v.boost_active || (v.boosted_until && v.boosted_until > new Date().toISOString()));
  // Demo / placeholder listing detection — either an explicit flag from the
  // backend or a `TEST` prefix on the make/model (legacy test data). These
  // should be visually flagged so users don't mistake them for real ads.
  const isDemo = Boolean(
    v.is_demo === true ||
    (typeof v.make === "string" && v.make.toUpperCase().startsWith("TEST")) ||
    (typeof v.model === "string" && v.model.toUpperCase().startsWith("TEST"))
  );
  const tr = (s) => localizeValue(s, lang);

  const onFavClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) { window.location.href = `/login?next=/vehicle/${v.id}`; return; }
    toggle(v.id);
  };

  // Fallback image (SVG data URL) — rendered when the vehicle has NO image
  // URLs at all, or when every URL we know about fails to load. Uses the
  // CARTAL palette so it still looks intentional rather than "broken".
  const FALLBACK_IMG =
    "data:image/svg+xml;charset=UTF-8," +
    encodeURIComponent(
      `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 800 450'>
        <defs>
          <linearGradient id='g' x1='0' x2='1' y1='0' y2='1'>
            <stop offset='0%' stop-color='#0a0a0a'/>
            <stop offset='60%' stop-color='#1a140a'/>
            <stop offset='100%' stop-color='#3d2f0e'/>
          </linearGradient>
        </defs>
        <rect width='800' height='450' fill='url(#g)'/>
        <text x='400' y='210' font-family='Cinzel,Playfair Display,serif' font-size='56' font-weight='900'
              text-anchor='middle' fill='#c9a55c' letter-spacing='6'>CARTAL</text>
        <text x='400' y='250' font-family='sans-serif' font-size='14' text-anchor='middle'
              fill='#c9a55c' letter-spacing='4' opacity='0.7'>DRIVE MORE · LIVE BETTER</text>
      </svg>`
    );

  // iter157 — image resolution:
  //   1. Merge every candidate URL we know about (single `image` + `images[]`)
  //      deduped, so we never miss a valid photo just because the primary
  //      `image` string is empty.
  //   2. On <img> error, hop to the next candidate BEFORE surrendering to
  //      the CARTAL fallback. Real listings with any working photo will
  //      show that photo instead of the black brand card.
  //
  // iter159 — thumbnail routing:
  //   Any URL pointing at our own file-proxy (`/api/files/...`) gets a
  //   `?w=800` suffix so the backend returns a compact WebP thumbnail
  //   instead of the 2-4 MB original. External URLs (unsplash, placehold,
  //   picsum, direct CDNs on other domains) pass through untouched.
  //   The vehicle DETAIL page renders the raw `v.images[]` URLs directly
  //   and NEVER goes through this component, so full-res quality there
  //   is preserved.
  const toThumbUrl = (u) => {
    if (typeof u !== "string") return u;
    // Only rewrite our own file proxy URLs (relative or absolute).
    if (!/(^|\/)api\/files\//.test(u)) return u;
    // If a width param is already there, respect it.
    if (/[?&]w=\d+/.test(u)) return u;
    return u + (u.includes("?") ? "&" : "?") + "w=800";
  };
  const candidates = React.useMemo(() => {
    const list = [];
    const push = (u) => {
      const s = typeof u === "string" ? u.trim() : "";
      if (s && !list.includes(s)) list.push(toThumbUrl(s));
    };
    push(v.image);
    (Array.isArray(v.images) ? v.images : []).forEach(push);
    return list;
  }, [v.image, v.images]);
  const primarySrc = candidates[0] || FALLBACK_IMG;
  const handleImgError = (e) => {
    const el = e.currentTarget;
    const nextIdx = Number(el.dataset.candIdx || 0) + 1;
    if (nextIdx < candidates.length) {
      el.dataset.candIdx = String(nextIdx);
      el.src = candidates[nextIdx];
      return;
    }
    // Exhausted every candidate → show CARTAL fallback exactly once.
    if (el.src !== FALLBACK_IMG) el.src = FALLBACK_IMG;
  };

  return (
    <Link
      to={`/vehicle/${v.id}`}
      className="block group"
      data-testid={`vehicle-card-${v.id}`}
    >
      <div className={`card-img relative rounded-xl border bg-white overflow-hidden ${boosted ? "border-[#c9a55c]/70 ring-2 ring-[#c9a55c]/30" : "border-[#0a0a0a]/[0.08]"}`}>
        <img
          src={primarySrc}
          data-cand-idx="0"
          onError={handleImgError}
          alt={`${v.make} ${v.model}`}
          loading="lazy"
          decoding="async"
          referrerPolicy="no-referrer"
          className={`listing-img ${v.is_sold ? "grayscale opacity-70" : ""}`}
        />
        {/* iter221 — «مباعة» overlay. Anchored dead-center over the
            image so it never overlaps corner badges (auction, demo,
            boosted, periodic, escrow, favourite). Renders on top of
            a slightly grayscaled image so the sold status is
            unmistakable at a glance. */}
        {v.is_sold && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-20"
               data-testid={`badge-sold-${v.id}`}>
            <span
              className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-black uppercase tracking-widest shadow-lg -rotate-6"
              style={{ background: "rgba(15,124,58,0.95)", color: "#fff", border: "2px solid rgba(255,255,255,0.85)" }}>
              <CheckCircle2 className="w-4 h-4" />
              {lang === "ar" ? "مباعة" : "SOLD"}
            </span>
          </div>
        )}
        {/* Demo listing badge — takes precedence over other badges */}
        {isDemo && (
          <span
            className="absolute top-3 start-3 inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-extrabold uppercase tracking-wider shadow-md z-10"
            style={{ background: "linear-gradient(135deg,#f59e0b,#b45309)", color: "#fff", border: "1px solid rgba(255,255,255,0.30)" }}
            title={lang === "ar" ? "إعلان تجريبي — للعرض فقط" : "Demo listing — display only"}
            data-testid={`badge-demo-${v.id}`}
          >
            {lang === "ar" ? "إعلان تجريبي" : "DEMO"}
          </span>
        )}
        {isAuction && !isDemo && (
          <span
            className="absolute top-3 start-3 inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-extrabold uppercase tracking-wider shadow-md animate-pulse"
            style={{ background: "linear-gradient(135deg,#dc2626,#9f1239)", color: "#fff", border: "1px solid rgba(255,255,255,0.25)" }}
            data-testid={`badge-auction-${v.id}`}
            title={lang === "ar" ? "مزاد مباشر" : "Live auction"}
          >
            <Gavel className="w-3 h-3" />
            {lang === "ar" ? "مزاد مباشر" : "Live auction"}
          </span>
        )}
        {boosted && !isAuction && !isDemo && (
          <span
            className="absolute top-3 start-3 inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-extrabold uppercase tracking-wider shadow-md"
            style={{ background: "linear-gradient(135deg,#c9a55c,#8a6f2e)", color: "#0a0a0a", border: "1px solid rgba(10,10,10,0.15)" }}
            data-testid={`badge-boosted-${v.id}`}
            title={lang === "ar" ? "إعلان مميّز" : "Boosted listing"}
          >
            <Sparkles className="w-3 h-3" />
            {lang === "ar" ? "مميّز" : "Featured"}
          </span>
        )}
        {!boosted && v.featured && (
          <span className="absolute top-3 start-3 pill pill-accent">{lang === "ar" ? "مميز" : "Featured"}</span>
        )}
        {periodicValid && (
          <span
            className="absolute top-3 end-3 inline-flex items-center gap-1 rounded-full px-2 py-1 text-[10px] font-bold uppercase tracking-wider"
            style={{ backgroundColor: "#1f6f3a", color: "#ffffff", border: "1px solid rgba(255,255,255,0.25)" }}
            title={lang === "ar" ? "فحص دوري (سلامة/فحص) ساري" : "Periodic inspection (Salama/Fahas) valid"}
            data-testid={`badge-periodic-${v.id}`}
          >
            <ShieldCheck className="w-3 h-3" style={{ color: "#ffffff" }} />
            {lang === "ar" ? "فحص دوري" : "Periodic OK"}
          </span>
        )}
        {FEATURES.ESCROW_ENABLED && escrowEnabled && (
          <span
            className={`absolute ${periodicValid ? "top-12" : "top-3"} end-3 inline-flex items-center gap-1 rounded-full px-2 py-1 text-[10px] font-bold uppercase tracking-wider`}
            style={{ backgroundColor: "#0a0a0a", color: "#d4ae6a", border: "1px solid #c9a55c" }}
            title={lang === "ar" ? "محمي بضمان كارتال (اختياري)" : "Protected by CARTAL Escrow (optional)"}
            data-testid={`badge-escrow-${v.id}`}
          >
            <ShieldCheck className="w-3 h-3" style={{ color: "#d4ae6a" }} />
            {lang === "ar" ? "ضمان كارتال" : "Escrow"}
          </span>
        )}
        <button
          type="button"
          onClick={onFavClick}
          className={`absolute bottom-3 end-3 w-9 h-9 rounded-full backdrop-blur-sm flex items-center justify-center transition-colors ${
            fav ? "bg-[#c9a55c] text-[#0a0a0a]" : "bg-white/85 text-[#0a0a0a] hover:bg-white"
          } shadow-sm border border-[#0a0a0a]/10`}
          aria-label="Favorite"
          data-testid={`fav-btn-${v.id}`}
        >
          <Heart className={`w-4 h-4 ${fav ? "fill-current" : ""}`} />
        </button>
      </div>
      <div className="pt-4 px-1">
        <div className="flex items-baseline justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="text-xs text-[#8a8a8a] flex items-center gap-2">
              <span>{v.year} · {v.make}</span>
              {v.ad_number && (
                <span
                  className="font-mono text-[10px] px-1.5 py-0.5 rounded border"
                  style={{ color: "#8a6f2e", borderColor: "rgba(201,165,92,0.4)", backgroundColor: "rgba(201,165,92,0.08)" }}
                  data-testid={`ad-number-${v.id}`}
                  title={lang === "ar" ? "الرقم الإعلاني" : "Ad number"}
                  dir="ltr"
                >
                  {v.ad_number}
                </span>
              )}
            </div>
            <h3 className="font-display text-lg font-medium leading-tight mt-0.5 truncate text-[#0a0a0a] group-hover:text-[#a8893e] transition-colors">
              {v.model}
            </h3>
          </div>
          <ArrowUpRight className="w-4 h-4 text-[#b3b3b3] group-hover:text-[#0a0a0a] shrink-0" />
        </div>
        {/* Phase 2C — 'مقدَّم من [Business]' badge */}
        {v.business && (
          <div
            className="mt-2 inline-flex items-center gap-1.5 max-w-full text-[10px] font-bold px-2 py-1 rounded-full"
            style={{
              background: "linear-gradient(135deg,rgba(232,200,115,0.14),rgba(201,165,92,0.06))",
              border: "1px solid rgba(201,165,92,0.35)",
              color: "#8a6f2e",
            }}
            data-testid={`card-business-badge-${v.id}`}
            title={lang === "ar" ? `مقدَّم من ${v.business.name_ar}` : `By ${v.business.name_en || v.business.name_ar}`}
          >
            <Building2 className="w-3 h-3 shrink-0" />
            <span className="truncate">
              {lang === "ar" ? `مقدَّم من ${v.business.name_ar}` : `By ${v.business.name_en || v.business.name_ar}`}
            </span>
            {v.business.verified && <CheckCircle2 className="w-3 h-3 shrink-0 text-emerald-600" />}
          </div>
        )}
        <div className="mt-3 flex items-baseline justify-between">
          <div className="text-xs text-[#8a8a8a]">{tr(v.city)} · {v.mileage_km > 0 ? `${v.mileage_km.toLocaleString()} ${t("common.km")}` : tr(v.fuel)}</div>
          <div className="text-end">
            {isAuction ? (
              <>
                <div className="text-[9px] uppercase tracking-widest font-bold text-[#dc2626]">
                  {lang === "ar"
                    ? (v.current_bid_sar ? `${v.bid_count || 0} مزايدة` : "سعر البدء")
                    : (v.current_bid_sar ? `${v.bid_count || 0} bid${(v.bid_count || 0) === 1 ? "" : "s"}` : "Starting")}
                </div>
                <span className="text-base font-semibold text-[#0a0a0a]">
                  {formatSAR(v.current_bid_sar || v.starting_bid_sar || v.price_sar)}
                </span>
                <span className="text-[11px] text-[#8a8a8a] ms-1">{lang === "ar" ? "ريال" : "SAR"}</span>
              </>
            ) : v.price_visibility === "hidden" ? (
              <>
                <span className="text-[13px] font-semibold text-[#8a6f2e]" data-testid={`card-price-hidden-${v.id}`}>
                  {lang === "ar" ? "السعر عند الطلب" : "Price on request"}
                </span>
              </>
            ) : (
              <>
                <span className="text-base font-semibold text-[#0a0a0a]">{formatSAR(v.price_sar)}</span>
                <span className="text-[11px] text-[#8a8a8a] ms-1">{lang === "ar" ? "ريال" : "SAR"}{isRental ? t("common.perDay") : ""}</span>
              </>
            )}
          </div>
        </div>
        {sellerVerified && (
          <div className="mt-2 inline-flex items-center gap-1 text-[10px] font-semibold rounded-full px-2 py-0.5"
            style={{ backgroundColor: "rgba(15,99,67,0.08)", color: "#0a4f3a", border: "1px solid rgba(15,99,67,0.25)" }}
            data-testid={`seller-verified-${v.id}`}
            title={lang === "ar" ? "بائع موثّق من كارتال (هوية معتمدة)" : "CARTAL-verified seller (KYC approved)"}
          >
            <ShieldCheck className="w-3 h-3" />
            {v.seller_kyc_type === "business"
              ? (lang === "ar" ? "تاجر موثّق" : "Verified Dealer")
              : (lang === "ar" ? "بائع موثّق" : "Verified Seller")}
          </div>
        )}
        {FEATURES.ESCROW_ENABLED && escrowVerified && (
          <div className={`${sellerVerified ? "ms-2" : "mt-2"} inline-flex items-center gap-1 text-[10px] font-bold rounded-full px-2 py-0.5`}
            style={{
              background: "linear-gradient(135deg,#c9a55c 0%,#8a6f2e 100%)",
              color: "#0a0a0a",
              border: "1px solid rgba(10,10,10,0.15)",
              boxShadow: "0 1px 4px rgba(201,165,92,0.35)",
            }}
            data-testid={`escrow-verified-${v.id}`}
            title={lang === "ar"
              ? "صفقة سابقة موثّقة بضمان كارتال — دليل ثقة إضافي"
              : "Previously verified via CARTAL Guarantee — extra trust signal"}
          >
            <ShieldCheck className="w-3 h-3" />
            {lang === "ar" ? "موثّق بضمان كارتال" : "CARTAL-Guaranteed"}
          </div>
        )}
      </div>
    </Link>
  );
}
